import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Package, Scan, CheckCircle, AlertCircle, ChevronRight, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const PickerGuidance = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const slipNo = searchParams.get('slip');
  const scanInputRef = useRef(null);

  const [slip, setSlip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staffId, setStaffId] = useState('');
  const [staffValidated, setStaffValidated] = useState(false);
  const [staffName, setStaffName] = useState('');
  
  const [currentBundle, setCurrentBundle] = useState(null);
  const [scannedCode, setScannedCode] = useState('');
  const [progress, setProgress] = useState({ picked: 0, total: 0, percentage: 0 });
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    if (slipNo) {
      fetchSlipDetails();
    } else {
      toast.error('No slip number provided');
      navigate('/wms/pick-slips');
    }
  }, [slipNo]);

  const fetchSlipDetails = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/wms/pick-slips/${slipNo}`);
      setSlip(response.data);
      
      if (response.data.status === 'COMPLETED') {
        setCompleted(true);
      } else if (response.data.status === 'IN_PROGRESS') {
        setStaffValidated(true);
        setStaffId(response.data.picked_by || '');
        setStaffName(response.data.picker_name || '');
        fetchNextBundle();
      }
    } catch (error) {
      toast.error('Failed to fetch slip details');
      navigate('/wms/pick-slips');
    } finally {
      setLoading(false);
    }
  };

  const fetchNextBundle = async () => {
    try {
      const response = await apiClient.get(`/wms/pick-slips/${slipNo}/next-bundle`);
      
      if (response.data.has_next) {
        setCurrentBundle(response.data.bundle);
        setProgress(response.data.progress);
        setTimeout(() => scanInputRef.current?.focus(), 100);
      } else {
        setCurrentBundle(null);
        setCompleted(true);
        setProgress({ picked: response.data.progress?.total || 0, total: response.data.progress?.total || 0, percentage: 100 });
      }
    } catch (error) {
      toast.error('Failed to get next bundle');
    }
  };

  const handleStaffValidate = async () => {
    if (!staffId.trim()) {
      toast.error('Please enter Staff ID');
      return;
    }

    try {
      const response = await apiClient.get(`/validate/staff/${staffId}`);
      if (response.data.valid) {
        setStaffValidated(true);
        setStaffName(response.data.staff_name);
        
        // Start the pick slip if pending
        if (slip.status === 'PENDING') {
          await apiClient.post(`/wms/pick-slips/${slipNo}/start?staff_id=${staffId}`);
          toast.success('Pick slip started');
        }
        
        fetchNextBundle();
      } else {
        toast.error('Invalid Staff ID');
      }
    } catch (error) {
      toast.error('Staff validation failed');
    }
  };

  const handleBundleScan = async () => {
    if (!scannedCode.trim()) {
      toast.error('Please scan bundle barcode');
      return;
    }

    if (scannedCode !== currentBundle?.bundle_code) {
      toast.error('Wrong bundle! Please scan the correct bundle.');
      setScannedCode('');
      scanInputRef.current?.focus();
      return;
    }

    try {
      const response = await apiClient.post(
        `/wms/pick-slips/${slipNo}/pick-bundle?bundle_code=${scannedCode}`
      );
      
      toast.success(response.data.message);
      setScannedCode('');
      
      if (response.data.slip_completed) {
        setCompleted(true);
        setCurrentBundle(null);
        setProgress({ ...progress, picked: progress.total, percentage: 100 });
      } else {
        fetchNextBundle();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pick bundle');
      setScannedCode('');
      scanInputRef.current?.focus();
    }
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
        <p className="text-[#5A5F6E]">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/pick-slips')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Picker Guidance</h1>
              <p className="text-sm text-[#E8DCC8]">Slip: {slipNo}</p>
            </div>
          </div>
          {staffValidated && (
            <div className="text-right">
              <p className="text-sm text-[#E8DCC8]">Picker</p>
              <p className="font-medium">{staffName}</p>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 md:px-6 py-8">
        {/* Progress Bar */}
        {staffValidated && !completed && (
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-4 mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-[#5A5F6E]">Progress</span>
              <span className="text-sm font-medium">{progress.picked} / {progress.total} bundles</span>
            </div>
            <Progress value={progress.percentage} className="h-3" />
          </div>
        )}

        {/* Completed Screen */}
        {completed && (
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#1A1F2E] mb-2">Pick Slip Completed!</h2>
            <p className="text-[#5A5F6E] mb-6">
              All {progress.total} bundles have been picked successfully.
            </p>
            <div className="flex gap-4 justify-center">
              <Button
                variant="outline"
                onClick={() => navigate('/wms/pick-slips')}
              >
                Back to Pick Slips
              </Button>
              <Button
                onClick={() => navigate('/wms')}
                className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
              >
                WMS Dashboard
              </Button>
            </div>
          </div>
        )}

        {/* Staff Validation */}
        {!staffValidated && !completed && (
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
            <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4">Staff Validation</h2>
            <p className="text-[#5A5F6E] mb-4">
              Please scan or enter your Staff ID to begin picking.
            </p>
            <div className="flex gap-3">
              <div className="flex-1">
                <Label>Staff ID</Label>
                <Input
                  value={staffId}
                  onChange={(e) => setStaffId(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, handleStaffValidate)}
                  placeholder="Scan or enter Staff ID"
                  className="mt-1"
                  autoFocus
                  data-testid="staff-id-input"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleStaffValidate}
                  className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                >
                  Start Picking
                </Button>
              </div>
            </div>

            {/* Slip Summary */}
            {slip && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-[#1A1F2E] mb-2">Slip Summary</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-[#5A5F6E]">SKU:</span>
                    <span className="ml-2 font-medium">{slip.sku_code}</span>
                  </div>
                  <div>
                    <span className="text-[#5A5F6E]">Design:</span>
                    <span className="ml-2">{slip.design_name || '-'}</span>
                  </div>
                  <div>
                    <span className="text-[#5A5F6E]">Shade:</span>
                    <span className="ml-2" data-testid="guidance-shade">{slip.shade || '-'}</span>
                  </div>
                  <div>
                    <span className="text-[#5A5F6E]">Size:</span>
                    <span className="ml-2" data-testid="guidance-size">{slip.size || '-'}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-[#5A5F6E]">Description:</span>
                    <span className="ml-2 text-xs" data-testid="guidance-description">{slip.description || '-'}</span>
                  </div>
                  <div>
                    <span className="text-[#5A5F6E]">Total Bundles:</span>
                    <span className="ml-2 font-medium">{slip.total_bundles}</span>
                  </div>
                  <div>
                    <span className="text-[#5A5F6E]">Total Qty:</span>
                    <span className="ml-2 font-medium">{slip.pick_qty?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Current Bundle to Pick */}
        {staffValidated && !completed && currentBundle && (
          <div className="space-y-6">
            {/* Go To Location */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-blue-600 font-medium">Step 1: Go to Location</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{currentBundle.bin_location}</p>
                </div>
              </div>
              <div className="flex items-center text-sm text-blue-700">
                <ChevronRight className="w-4 h-4 mr-1" />
                Navigate to the bin location shown above
              </div>
            </div>

            {/* Bundle Info */}
            <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <Package className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-purple-600 font-medium">Step 2: Pick Bundle</p>
                  <p className="text-xl font-bold font-mono text-[#1A1F2E]">{currentBundle.bundle_code}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-[#5A5F6E]">Quantity</p>
                  <p className="font-bold text-lg">{currentBundle.usn_count} pcs</p>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-[#5A5F6E]">Sequence</p>
                  <p className="font-bold text-lg">#{currentBundle.pick_sequence} of {progress.total}</p>
                </div>
              </div>
            </div>

            {/* Scan Bundle */}
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Scan className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-green-600 font-medium">Step 3: Scan to Confirm</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Input
                    ref={scanInputRef}
                    value={scannedCode}
                    onChange={(e) => setScannedCode(e.target.value)}
                    onKeyPress={(e) => handleKeyPress(e, handleBundleScan)}
                    placeholder="Scan bundle barcode"
                    className="text-lg h-12"
                    data-testid="bundle-scan-input"
                  />
                </div>
                <Button
                  onClick={handleBundleScan}
                  className="bg-green-600 hover:bg-green-700 text-white h-12 px-6"
                  data-testid="confirm-pick-btn"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Confirm
                </Button>
              </div>
              <p className="text-sm text-green-700 mt-2">
                Scan the bundle barcode to confirm pickup
              </p>
            </div>

            {/* Hint */}
            <div className="flex items-start gap-2 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Tip</p>
                <p>Make sure to scan the correct bundle. The system will validate the barcode before confirming the pick.</p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PickerGuidance;
